import React, { useEffect, useRef, useState } from "react";
import {
  ContextualMenu,
  DirectionalHint,
  Icon,
  mergeStyles,
} from "@fluentui/react";

// Grace period so the pointer can travel from the row into the submenu.
const CLOSE_DELAY_MS = 150;

/**
 * Context-menu row whose submenu is open only while the pointer is over the
 * row or the submenu. Fluent's built-in subMenuProps keeps the submenu open
 * after the pointer leaves, which we don't want here.
 */
export function HoverSubmenuMenuItem({
  text,
  items,
  disabled = false,
  title,
  theme,
  menuType,
  menuStyles,
  dismissMenu,
}) {
  const rowRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  // Only move focus into the submenu when it was opened from the keyboard.
  const [focusOnOpen, setFocusOnOpen] = useState(false);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const show = (fromKeyboard = false) => {
    if (disabled) return;
    cancelClose();
    setFocusOnOpen(fromKeyboard);
    setOpen(true);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => cancelClose, []);

  const rowClass = mergeStyles({
    display: "flex",
    alignItems: "center",
    width: "100%",
    height: 36,
    padding: "0 6px",
    boxSizing: "border-box",
    border: "none",
    background: open
      ? theme.colors.contextMenuHover
      : theme.colors.contextMenuBackground,
    color: disabled ? theme.colors.deactiveColor : theme.colors.fontColor,
    cursor: disabled ? "not-allowed" : "pointer",
    textAlign: "left",
    outline: "none",
    ...menuType,
    selectors: {
      ":hover": {
        backgroundColor: disabled
          ? theme.colors.deactiveColorLight
          : theme.colors.contextMenuHover,
      },
    },
  });

  // Clicking a submenu entry runs its action and closes the whole menu.
  const subItems = items.map((item) => ({
    ...item,
    onClick: () => {
      item.onClick?.();
      setOpen(false);
      dismissMenu?.();
    },
  }));

  return (
    <>
      <button
        ref={rowRef}
        type="button"
        className={rowClass}
        title={title}
        aria-disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseEnter={() => show()}
        onMouseLeave={scheduleClose}
        onClick={() => show()}
        onKeyDown={(ev) => {
          if (ev.key === "ArrowRight" || ev.key === "Enter" || ev.key === " ") {
            ev.preventDefault();
            show(true);
          }
        }}
      >
        <span style={{ flex: 1, margin: "0 4px" }}>{text}</span>
        <Icon
          iconName="ChevronRight"
          styles={{ root: { fontSize: 12, margin: "0 4px" } }}
        />
      </button>
      {open && (
        <ContextualMenu
          target={rowRef}
          items={subItems}
          styles={menuStyles}
          directionalHint={DirectionalHint.rightTopEdge}
          isBeakVisible={false}
          gapSpace={0}
          shouldFocusOnMount={focusOnOpen}
          onDismiss={() => setOpen(false)}
          onRenderMenuList={(listProps, defaultRender) => (
            <div onMouseEnter={() => show()} onMouseLeave={scheduleClose}>
              {defaultRender(listProps)}
            </div>
          )}
        />
      )}
    </>
  );
}

export default HoverSubmenuMenuItem;
